// THE TEMPLATE RENDERER — one implementation, every screen that renders a canonical document.
//
// It lived inside VaultForm, which is where documents are printed for real. The full-template editor
// needs to render the same HTML against a canonical sample so the author can see what they are building,
// and a second copy of a renderer is the worst kind of duplicate: the two drift, and the difference only
// shows up as a folio that looked right in the editor and wrong in the guest's hand.
//
// The language is deliberately tiny, and this file is its whole definition:
//   {{path.to.value}}                    one value. Dot paths index objects; a numeric segment indexes an
//                                        array; `*` scans an array for the first item with a value.
//   {{#each path.to.array}}…{{/each}}    repeat, with variables inside resolved against the item.
//   {{#if path}}…{{/if}}                 include when the value is present.
// No arithmetic, no helpers — a value must already exist in the record. The one formatting rule is the
// TEMPLATE'S AMOUNT FORMAT (below), a setting of the template rather than syntax in it.

export function flattenObject(obj, prefix = '') {
  const result = {};
  for (const [key, value] of Object.entries(obj || {})) {
    const full = prefix ? `${prefix}.${key}` : key;
    if (Array.isArray(value)) {
      value.forEach((item, i) => {
        if (item !== null && typeof item === 'object') {
          Object.assign(result, flattenObject(item, `${full}.${i}`));
        } else {
          result[`${full}.${i}`] = item != null ? String(item) : '';
        }
      });
    } else if (value !== null && typeof value === 'object') {
      Object.assign(result, flattenObject(value, full));
    } else {
      result[full] = value != null ? String(value) : '';
    }
  }
  return result;
}

// Traverse a nested object by dot-notation path (numeric keys index into arrays).
// A path segment of '*' iterates an array and returns the first item whose remaining path resolves to a
// non-null, non-empty value.
export function getNestedValue(obj, path) {
  const parts = String(path).trim().split('.');
  const traverse = (current, rest) => {
    if (current === null || current === undefined) return null;
    if (rest.length === 0) return current;
    const [head, ...tail] = rest;
    if (head === '*') {
      if (!Array.isArray(current)) return null;
      for (const item of current) {
        const val = traverse(item, tail);
        if (val !== null && val !== undefined && val !== '') return val;
      }
      return null;
    }
    const i = parseInt(head, 10);
    return traverse((!isNaN(i) && Array.isArray(current)) ? current[i] : current?.[head], tail);
  };
  return traverse(obj, parts);
}

// HOW A TEMPLATE PRINTS MONEY (v2_hotel_full_templates, V619): fixed decimal places and the separators a
// property writes — 285.00, 1.234,50, 1'234.50. Applied to every numeric value whose field name ends in
// "amount" (amount, taxAmount, netAmount, grossAmount …); rates, quantities and ids are left as they are.
// A template with nothing set prints values exactly as stored, as every template did before.
//
// SHOW ZERO AMOUNTS (show_zero_lines, V620) travels with it: off, an {{#each}} item whose amounts are all
// zero — a 0.00 package component, a zero posting — is left out of the document (hasOnlyZeroAmounts).
export function amountFormatOf(template) {
  if (!template) return null;
  const decimals = template.amount_decimals === null || template.amount_decimals === undefined || template.amount_decimals === ''
    ? null : Number(template.amount_decimals);
  const decimalSeparator = template.decimal_separator || '.';
  const thousandsSeparator = template.thousands_separator || '';
  const hideZeroLines = template.show_zero_lines === 'N';
  if (decimals === null && decimalSeparator === '.' && !thousandsSeparator && !hideZeroLines) return null;
  return { decimals, decimalSeparator, thousandsSeparator, hideZeroLines };
}

// An item that HAS amounts and every one of them is zero. One with no amount fields at all (a profile, a
// phone number) is never "a zero line", so nothing but money lines is ever hidden.
const hasOnlyZeroAmounts = (item) => {
  // A charge line says so itself (vaultfolio: nothing in its Charges or Credits column). A package component
  // carries a posted amount and a package debit, so judging it by every amount field kept it.
  if (typeof item.zeroAmount === 'boolean') return item.zeroAmount;
  const amounts = Object.entries(flattenObject(item))
    .filter(([k, v]) => AMOUNT_KEY.test(k.split('.').pop()) && NUMERIC.test(String(v).trim()))
    .map(([, v]) => Number(v));
  return amounts.length > 0 && amounts.every(n => n === 0);
};

const AMOUNT_KEY = /amount$/i;
const NUMERIC = /^-?\d+(\.\d+)?$/;
export function formatAmount(value, key, fmt) {
  if (!fmt || value === null || value === undefined) return value;
  const last = String(key).trim().split('.').pop();
  const text = String(value).trim();
  if (!AMOUNT_KEY.test(last) || !NUMERIC.test(text)) return value;
  const n = Number(text);
  const fixed = fmt.decimals === null ? text.replace(/^-/, '') : Math.abs(n).toFixed(fmt.decimals);
  const [whole, frac] = fixed.split('.');
  const grouped = fmt.thousandsSeparator ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, fmt.thousandsSeparator) : whole;
  return `${n < 0 ? '-' : ''}${grouped}${frac !== undefined ? fmt.decimalSeparator + frac : ''}`;
}

export function decodeHtml(html) {
  const ta = document.createElement('textarea');
  ta.innerHTML = html;
  return ta.value;
}

const parsePayload = (payload) => {
  if (!payload) return null;
  if (typeof payload !== 'string') return payload;
  try { return JSON.parse(payload); } catch (e) { return null; }
};

// Resolve one variable. Order: a named scope (hotel.*, account.*), then the canonical payload by dot
// path, then the flat row's columns case-insensitively — the order the vault screens have always used.
export function makeResolveVar({ row, payload, scopes = {} }) {
  const parsed = parsePayload(payload);
  return (key) => {
    const trimmed = String(key).trim();

    for (const [name, source] of Object.entries(scopes)) {
      const prefix = `${name}.`;
      if (trimmed.startsWith(prefix) && source) {
        const field = trimmed.slice(prefix.length);
        const k = Object.keys(source).find(c => c.toLowerCase() === field.toLowerCase());
        if (k !== undefined && source[k] !== null && source[k] !== '') return String(source[k]);
        return null;
      }
    }

    if (parsed) {
      const val = getNestedValue(parsed, trimmed);
      if (val !== null && val !== undefined && typeof val !== 'object') return String(val);
    }

    if (row) {
      const colKey = Object.keys(row).find(k => k.toLowerCase() === trimmed.toLowerCase());
      if (colKey !== undefined && row[colKey] !== null) return String(row[colKey]);
    }

    return null;
  };
}

export function processEach(html, payload, fmt = null) {
  const parsed = parsePayload(payload);
  if (!html || !parsed) return html;
  return html.replace(/\{\{#each ([^}]+)\}\}([\s\S]*?)\{\{\/each\}\}/g, (match, path, itemTemplate) => {
    const arr = getNestedValue(parsed, path.trim());
    if (!Array.isArray(arr) || arr.length === 0) return '';
    const items = fmt && fmt.hideZeroLines ? arr.filter(item => !(item && typeof item === 'object' && hasOnlyZeroAmounts(item))) : arr;
    return items.map(item => expandItem(itemTemplate, item, fmt)).join('');
  });
}

// One repeated row.
//
// TWO RULES THAT WERE MISSING, both of which silently produced wrong documents:
//
//   {{#if key}}…{{/if}} INSIDE a repeat is now evaluated against the ITEM. Before, the substitution below
//   matched the marker itself (the regex accepts any {{…}}), found no such key on the item, and replaced it
//   with an empty string — so the guard vanished and its body ALWAYS printed. A folio line ended up showing
//   its description, its fallback description and its raw reference all at once because all three were
//   wrapped in ifs that did nothing.
//
//   A token the item does NOT have is LEFT ALONE rather than blanked. It may belong to the document —
//   {{#if showLineVat}} is a property of the template, not of a charge line — and the later passes
//   (processConditionals, then the plain substitution) resolve it against the payload and the row. Blanking
//   it here made anything outside the item unusable inside a repeat.
// Innermost {{#if}} block: its body contains no further {{#if}}, so repeated application resolves a
// nest from the inside out. Matching the outer one first would pair it with the INNER {{/if}} and leave
// the outer close behind — which is exactly how a stray </td> ended up in a rendered folio.
const INNERMOST_IF = /\{\{#if ([^}]+)\}\}((?:(?!\{\{#if )[\s\S])*?)\{\{\/if\}\}/;

// 'N' IS FALSE. Every flag in this product is a CHAR(1) 'Y'/'N' — v2_hotel_full_templates.show_line_vat,
// vaulttable.extract, configured_only — and a plain truthiness test makes 'N' true because it is a
// non-empty string, so a switch turned off behaved exactly like one turned on.
const FALSE_WORDS = new Set(['n', 'no', 'false', '0', 'off']);
const truthy = (v) => {
    if (v === null || v === undefined || v === '' || v === false || v === 0) return false;
    return !FALSE_WORDS.has(String(v).trim().toLowerCase());
};

// Resolve {{#if}} blocks innermost-first. `decide(key)` returns true, false, or the string 'skip' for a
// guard this pass does not own — a skipped block is parked under a placeholder so the loop can move past
// it without re-matching, and restored verbatim at the end for the next pass to handle.
function resolveIfs(html, decide) {
  const parked = [];
  let out = html;
  for (let guard = 0; guard < 500; guard += 1) {
    const m = INNERMOST_IF.exec(out);
    if (!m) break;
    const verdict = decide(m[1].trim());
    let replacement;
    if (verdict === 'skip') {
      parked.push(m[0]);
      replacement = `\u0000IF${parked.length - 1}\u0000`;
    } else {
      replacement = verdict ? m[2] : '';
    }
    out = out.slice(0, m.index) + replacement + out.slice(m.index + m[0].length);
  }
  return out.replace(/\u0000IF(\d+)\u0000/g, (_, i) => parked[Number(i)]);
}

// One repeated row.
//
// TWO RULES THAT WERE MISSING, both of which silently produced wrong documents:
//
//   {{#if key}}…{{/if}} INSIDE a repeat is now evaluated against the ITEM. Before, the substitution below
//   matched the marker itself (the regex accepts any {{…}}), found no such key on the item and replaced it
//   with an empty string — so the guard vanished and its body ALWAYS printed. A folio line showed its
//   description, its fallback description and its raw reference at once because all three were wrapped in
//   ifs that did nothing.
//
//   A token the item does NOT have is LEFT ALONE rather than blanked. It may belong to the document —
//   {{#if showLineVat}} is a property of the template, not of a charge line — and the later passes resolve
//   it against the payload and the row. Blanking it here made anything outside the item unusable inside a
//   repeat.
function expandItem(itemTemplate, item, fmt = null) {
  const flat = flattenObject(item);
  const has = (key) => (key in flat) || getNestedValue(item, key) !== null;
  const value = (key) => flat[key] ?? getNestedValue(item, key);

  const withIfs = resolveIfs(itemTemplate, key => (has(key) ? truthy(value(key)) : 'skip'));

  return withIfs.replace(/\{\{([^}]+)\}\}/g, (m, key) => {
    const trimmed = key.trim();
    if (trimmed.startsWith('#') || trimmed.startsWith('/')) return m;   // a marker for a later pass
    if (!has(trimmed)) return m;                                        // not this item's field
    return formatAmount(flat[trimmed] ?? String(getNestedValue(item, trimmed) ?? ''), trimmed, fmt);
  });
}

// Document-level conditionals, also innermost-first and for the same reason.
export function processConditionals(html, resolve) {
  if (!html) return html;
  return resolveIfs(html, key => truthy(resolve(key)));
}

// ctx: { row, payload, scopes, amountFormat } — see makeResolveVar; amountFormat from amountFormatOf(template).
export function renderTemplate(content, ctx = {}) {
  if (!content) return '';
  const resolve = makeResolveVar(ctx);
  const fmt = ctx.amountFormat || null;
  const decoded = decodeHtml(content);
  const withEach = processEach(decoded, ctx.payload, fmt);
  const withConditionals = processConditionals(withEach, resolve);
  return withConditionals.replace(/\{\{([^}]+)\}\}/g, (match, key) => formatAmount(resolve(key), key, fmt) ?? '');
}

// Open a rendered document in a print window. "Print / Save PDF" because the browser's print dialog is
// where a PDF actually comes from — a page cannot write one directly.
export function openPrintWindow({ name, html, header = '', footer = '' }) {
  const win = window.open('', '_blank');
  win.document.write(`<!DOCTYPE html><html><head><title>${name || 'Document'}</title><style>body{font-family:sans-serif;padding:20px;}@media print{body{margin:0;}}</style></head><body>${header}${html}${footer}</body></html>`);
  win.document.close();
  win.focus();
  win.onload = () => win.print();
}
